import { zodResolver } from '@hookform/resolvers/zod'
import type { FieldLevel, FieldSection, Meta, ValidationIssue } from '@numistic/contracts'
import { buildAttributesSchema, fieldsForType, type ResolvedField } from '@numistic/domain'
import { useMeta } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import { Button, Label } from '@numistic/ui'
import { useEffect, useId, useMemo, type ReactNode } from 'react'
import {
  Controller,
  useForm,
  type FieldErrors,
  type FieldValues,
  type Resolver,
} from 'react-hook-form'

import { useErrorText } from '../lib/errors.js'
import { displayValue } from './display.js'
import { useRefResolver } from './ref-resolver.js'
import { widgets } from './widgets/index.js'

export interface MetaFormProps {
  typeCode: string
  level: FieldLevel
  /** 'all' — все поля формы; 'attributes' — только attributes jsonb. */
  storage?: 'all' | 'attributes'
  defaultValues?: Record<string, unknown>
  onSubmit: (values: Record<string, unknown>) => void | Promise<void>
  submitLabel?: string
  /** Запрос отправлен (mutation.isPending): кнопка недоступна — без двойной отправки. */
  pending?: boolean
  /** Дополнительные элементы под кнопкой (ошибки сервера и т.п.). */
  footer?: ReactNode
  /** Подготовка значений перед проверкой (например, автоназвание по шаблону). */
  prepare?: (values: Record<string, unknown>) => Record<string, unknown>
  /** Ошибки проверки с сервера — показываются у своих полей. */
  serverIssues?: ValidationIssue[]
  /** Дополнительные поля над секциями (например, выбор видимости). */
  header?: ReactNode
  /** Блок между полями и кнопкой (например, «Покупка» при добавлении экземпляра). */
  beforeSubmit?: ReactNode
  /** Для экземпляра: показывать ли поля каталога для переопределения (по умолчанию да). */
  includeOverrides?: boolean
  /**
   * Значения из каталога (и разновидности) для полей-переопределений: под полем — «Из каталога:
   * …», чтобы было видно, что будет, если поле оставить пустым.
   */
  inherited?: Record<string, unknown>
  /** Порядок разделов (например, у экземпляра — сначала «Экземпляр»). */
  sectionOrder?: FieldSection[]
}

/** Форма любого типа объекта, собранная из метаданных (docs/PLAN.md, раздел 8.2). */
export function MetaForm(props: MetaFormProps) {
  const { t } = useTranslation()
  const meta = useMeta()
  if (!meta.data) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
  return <MetaFormInner meta={meta.data} {...props} />
}

function MetaFormInner({
  meta,
  typeCode,
  level,
  storage = 'all',
  defaultValues,
  onSubmit,
  submitLabel,
  pending = false,
  footer,
  prepare,
  serverIssues,
  header,
  beforeSubmit,
  includeOverrides = true,
  inherited,
  sectionOrder,
}: MetaFormProps & { meta: Meta }) {
  const { t } = useTranslation()
  const fields = useMemo(
    () =>
      fieldsForType(meta, typeCode, level).filter(
        (f) =>
          (storage === 'all' || f.def.storage.kind === 'attributes') &&
          (includeOverrides || !f.override),
      ),
    [meta, typeCode, level, storage, includeOverrides],
  )
  const visibleCodes = useMemo(() => new Set(fields.map((f) => f.def.code)), [fields])
  const schema = useMemo(
    () => buildAttributesSchema(meta, typeCode, level, { storage }),
    [meta, typeCode, level, storage],
  )
  const resolver = useMemo<Resolver<FieldValues>>(() => {
    const base = zodResolver(schema) as unknown as Resolver<FieldValues>
    return (values, context, options) => base(prepare ? prepare(values) : values, context, options)
  }, [schema, prepare])
  const form = useForm<FieldValues>({ resolver, defaultValues: defaultValues ?? {} })

  useEffect(() => {
    for (const issue of serverIssues ?? []) {
      const field = issue.path[0]
      if (typeof field === 'string')
        form.setError(field, { type: 'server', message: issue.message })
    }
  }, [serverIssues, form])

  const sections = useMemo(() => {
    const groups = new Map<FieldSection, ResolvedField[]>()
    for (const f of fields) groups.set(f.section, [...(groups.get(f.section) ?? []), f])
    const order = [...new Set([...(sectionOrder ?? []), ...groups.keys()])]
    return order.filter((x) => groups.has(x)).map((x) => [x, groups.get(x)!] as const)
  }, [fields, sectionOrder])
  const kinds = useMemo(() => new Set(fields.map((f) => f.def.refKind ?? '')), [fields])
  const resolve = useRefResolver(meta, kinds, { own: true })
  const inheritedHint = (f: ResolvedField) => {
    if (!f.override || !inherited) return undefined
    const text = displayValue(meta, f.def, inherited[f.def.code], resolve)
    return text ? t('meta.inherited', { value: text }) : undefined
  }
  /** Скрытые поля отправляются как были — иначе их сохранённые значения стёрлись бы. */
  const hiddenDefaults = Object.fromEntries(
    Object.entries(defaultValues ?? {}).filter(([code]) => !visibleCodes.has(code)),
  )

  return (
    <form
      noValidate
      className="grid gap-8"
      onSubmit={form.handleSubmit((values) =>
        // Поля формы — как ввели; скрытые (свёрнутые переопределения) — без изменений.
        onSubmit({
          ...hiddenDefaults,
          ...Object.fromEntries(
            Object.entries(values as Record<string, unknown>).filter(([code]) =>
              visibleCodes.has(code),
            ),
          ),
        }),
      )}
    >
      {header}
      {sections.map(([section, sectionFields]) => (
        <fieldset key={section} className="grid gap-4">
          <legend className="mb-2 text-sm font-semibold text-muted-foreground">
            {t(`sections.${section}`)}
          </legend>
          <div className="grid gap-4 md:grid-cols-2">
            {sectionFields.map((f) => (
              <Controller
                key={f.def.code}
                name={f.def.code}
                control={form.control}
                render={({ field, fieldState }) => (
                  <MetaField
                    meta={meta}
                    field={f}
                    typeCode={typeCode}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={(form.formState.errors as FieldErrors)[f.def.code] ?? fieldState.error}
                    hint={inheritedHint(f)}
                  />
                )}
              />
            ))}
          </div>
        </fieldset>
      ))}
      {beforeSubmit}
      <div className="grid gap-3">
        <Button
          type="submit"
          className="justify-self-start"
          disabled={form.formState.isSubmitting || pending}
        >
          {submitLabel ?? t('common.save')}
        </Button>
        {footer}
      </div>
    </form>
  )
}

const WIDE = new Set(['longtext', 'multi_enum', 'list'])

export function MetaField({
  meta,
  field,
  typeCode,
  value,
  onChange,
  onBlur,
  error,
  hint,
}: {
  meta: Meta
  field: ResolvedField
  typeCode?: string
  value: unknown
  onChange: (value: unknown) => void
  onBlur?: () => void
  error?: unknown
  /** Подсказка под полем (например, значение из каталога). */
  hint?: string
}) {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const id = useId()
  const message = errorText.field(error as never)
  const Widget = widgets[field.def.dataType]
  return (
    <div className={WIDE.has(field.def.dataType) ? 'grid gap-1.5 md:col-span-2' : 'grid gap-1.5'}>
      <Label htmlFor={id}>
        {field.def.name.ru}
        {field.required ? <span className="sr-only"> ({t('meta.required')})</span> : null}
        {field.required ? (
          <span className="ml-0.5 text-destructive" aria-hidden>
            *
          </span>
        ) : null}
      </Label>
      <Widget
        id={id}
        meta={meta}
        field={field}
        typeCode={typeCode}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        invalid={Boolean(message)}
        describedBy={message ? `${id}-error` : hint ? `${id}-hint` : undefined}
      />
      {hint && !message ? (
        <p id={`${id}-hint`} className="line-clamp-2 text-xs text-muted-foreground" title={hint}>
          {hint}
        </p>
      ) : null}
      {message ? (
        <p id={`${id}-error`} className="text-xs text-destructive">
          {message}
        </p>
      ) : null}
    </div>
  )
}
